import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import Dropzone from 'react-dropzone';
import classnames from 'classnames';
import { string, shape, bool, func, oneOf } from 'prop-types';
import { get } from 'lodash';
import { Loader, Button } from '@tesla/design-system-react';
import { v4 } from 'uuid';
import styles from './styles.module.css.js';

var propTypes = {
  /** Custom class name for container div */
  className: string,
  /** Text strings for all labels */
  content: shape({
    previewLabel: string,
    getPhoto: string,
    removePhoto: string,
    header: string,
    description: string
  }),
  /** Error message to display. Causes error state when present */
  error: string,
  /** Image URI to display */
  image: string,
  /** Toggles loading state */
  loading: bool,
  /** Callback triggered on add button interaction */
  onAddButtonClick: func,
  /** Callback triggered on dropzone interaction */
  onDrop: func,
  /** Callback triggered on remove button interaction */
  onRemoveButtonClick: func,
  /** Toggles remove button */
  showRemoveButton: bool,
  /** CSS background-size styling for image */
  imageSize: oneOf(['auto', 'cover', 'contain'])
};
var defaultProps = {
  className: '',
  content: {},
  error: '',
  image: '',
  loading: false,
  onAddButtonClick: function onAddButtonClick() {},
  onDrop: function onDrop() {},
  onRemoveButtonClick: function onRemoveButtonClick() {},
  showRemoveButton: false,
  imageSize: 'auto'
};
var PictureUploader = function PictureUploader(_ref) {
  var className = _ref.className,
    content = _ref.content,
    error = _ref.error,
    image = _ref.image,
    loading = _ref.loading,
    onAddButtonClick = _ref.onAddButtonClick,
    onDrop = _ref.onDrop,
    onRemoveButtonClick = _ref.onRemoveButtonClick,
    showRemoveButton = _ref.showRemoveButton,
    imageSize = _ref.imageSize;
  var dropzoneRef = /*#__PURE__*/React.createRef();
  var id = v4();
  var handleDrop = function handleDrop(files) {
    onDrop(files[0]);
  };
  var handleAddButtonClick = function handleAddButtonClick(event) {
    get(dropzoneRef, 'current.open', function () {})();
    onAddButtonClick(event);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(className, styles.PictureUploader, 'tds-scrim--white', 'tds-text--start', 'tds-text_color--30', {
      error: error
    })
  }, /*#__PURE__*/React.createElement(Dropzone, {
    onDrop: handleDrop,
    multiple: false,
    className: styles.Dropzone,
    noClick: true,
    ref: dropzoneRef,
    disabled: loading
  }, function (_ref2) {
    var getRootProps = _ref2.getRootProps,
      getInputProps = _ref2.getInputProps;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", _extends({
      "data-test": "picture-uploader-image-wrapper",
      className: styles.ImageWrapper
    }, getRootProps()), /*#__PURE__*/React.createElement("input", _extends({
      "aria-hidden": "true",
      title: "picture-uploader-file-input-".concat(id),
      "data-test": "picture-uploader-file-input"
    }, getInputProps())), /*#__PURE__*/React.createElement("div", {
      className: styles.Image,
      style: {
        backgroundImage: "url(".concat(image, ")"),
        backgroundSize: imageSize
      },
      "aria-label": error || content.previewLabel,
      role: "img"
    }, error && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: styles.ImageError,
      role: "alert"
    }, error), /*#__PURE__*/React.createElement("div", {
      className: styles.ErrorScrim
    }))), /*#__PURE__*/React.createElement(Loader, {
      show: loading,
      className: styles.LoadingSpinner
    }), showRemoveButton && /*#__PURE__*/React.createElement(Button, {
      type: "button",
      className: styles.RemoveButton,
      variant: "link",
      onClick: onRemoveButtonClick,
      "aria-label": content.removePhoto,
      disabled: loading,
      "data-test": "picture-uploader-remove-button"
    })), /*#__PURE__*/React.createElement("div", _extends({
      "data-test": "picture-uploader-details",
      className: styles.Details
    }, getRootProps(), {
      tabIndex: -1
    }), /*#__PURE__*/React.createElement("div", {
      className: styles.DetailsBlock
    }, /*#__PURE__*/React.createElement("h2", {
      className: classnames('tds-text--h6', styles.TextSubheading)
    }, content.header), /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: handleAddButtonClick,
      className: classnames('tds-link', 'tds-link--primary', styles.UploadLink),
      disabled: loading,
      "data-test": "picture-uploader-upload-link"
    }, content.getPhoto)), content.description && /*#__PURE__*/React.createElement("div", {
      className: "tds-text--caption"
    }, content.description)));
  }));
};
PictureUploader.propTypes = propTypes;
PictureUploader.defaultProps = defaultProps;

export { PictureUploader as default };
